// Unifies all input into a single normalized "target distance" signal in
// [0, 1] where 0 = marshmallow at the flame (hottest) and 1 = far away
// (coolest). Sources:
//   - Touch / mouse: press-drag — the marshmallow follows the pointer's
//     vertical position within the playfield.
//   - Keyboard: Up/W move away from the flame (cooler), Down/S move toward it
//     (hotter).
// The raw target is smoothed toward each frame so motion feels analog rather
// than instant. PlayScene reads getTargetDistance() every update().

import Phaser from 'phaser';
import { markTouchInput } from './device';

/** Distance change per second while a movement key is held. */
const KEY_SPEED = 0.9;
/** Smoothing rate — higher snaps to target faster (per second). */
const SMOOTH_RATE = 12;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

export class Controller {
  private target: number;
  private current: number;
  private playfieldTop = 0;
  private playfieldBottom = 1;
  private pointerActive = false;

  private keyUp?: Phaser.Input.Keyboard.Key;
  private keyDown?: Phaser.Input.Keyboard.Key;
  private keyW?: Phaser.Input.Keyboard.Key;
  private keyS?: Phaser.Input.Keyboard.Key;

  constructor(
    private scene: Phaser.Scene,
    initial = 0.6,
  ) {
    this.target = clamp01(initial);
    this.current = this.target;

    const kb = scene.input.keyboard;
    if (kb) {
      this.keyUp = kb.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
      this.keyDown = kb.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN);
      this.keyW = kb.addKey(Phaser.Input.Keyboard.KeyCodes.W);
      this.keyS = kb.addKey(Phaser.Input.Keyboard.KeyCodes.S);
    }

    scene.input.on(Phaser.Input.Events.POINTER_DOWN, this.onPointer, this);
    scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.onPointer, this);
    scene.input.on(Phaser.Input.Events.POINTER_UP, this.onPointerUp, this);
  }

  /** Define the vertical screen band the pointer maps across (pixels). */
  setPlayfield(topY: number, bottomY: number): void {
    this.playfieldTop = topY;
    this.playfieldBottom = bottomY;
  }

  private onPointer(pointer: Phaser.Input.Pointer): void {
    if (!pointer.isDown) return;
    if (pointer.wasTouch) markTouchInput();
    this.pointerActive = true;
    const span = this.playfieldBottom - this.playfieldTop;
    if (span <= 0) return;
    // Top of playfield = far (1), bottom (near flame) = 0.
    const frac = (pointer.y - this.playfieldTop) / span;
    this.target = clamp01(1 - frac);
  }

  private onPointerUp(): void {
    this.pointerActive = false;
  }

  /** Advance smoothing and apply held-key movement. Call once per frame. */
  update(dt: number): void {
    if (!this.pointerActive) {
      const up = this.keyUp?.isDown || this.keyW?.isDown;
      const down = this.keyDown?.isDown || this.keyS?.isDown;
      if (up) this.target = clamp01(this.target + KEY_SPEED * dt);
      if (down) this.target = clamp01(this.target - KEY_SPEED * dt);
    }
    const k = Math.min(1, SMOOTH_RATE * dt);
    this.current += (this.target - this.current) * k;
  }

  /** Smoothed normalized distance from the flame, [0, 1]. */
  getTargetDistance(): number {
    return this.current;
  }

  destroy(): void {
    this.scene.input.off(Phaser.Input.Events.POINTER_DOWN, this.onPointer, this);
    this.scene.input.off(Phaser.Input.Events.POINTER_MOVE, this.onPointer, this);
    this.scene.input.off(Phaser.Input.Events.POINTER_UP, this.onPointerUp, this);
  }
}
